import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Modal } from '@hotjar/hotsauce';

import { useNotify } from '@hotjar/webapp/hooks/useNotify';

import { useCollectionRemove } from './use-collection-remove';

interface RemovalConfirmationProps {
  collectionId: Parameters<typeof useCollectionRemove>[1];
  collectionName: string;
  isOpen: boolean;
  onClose: () => void;
  siteId: Parameters<typeof useCollectionRemove>[0];
}

export function RemovalConfirmation({
  collectionId,
  collectionName,
  isOpen,
  onClose,
  siteId,
}: RemovalConfirmationProps): JSX.Element {
  const { t } = useTranslation();
  const { success, error } = useNotify();
  const { mutateAsync: remove, status } = useCollectionRemove(siteId, collectionId);
  const isLoading = status === 'loading';

  const handleRemove = useCallback(async () => {
    try {
      await remove({});
      success(t('collections:remove.success'));
      onClose();
    } catch {
      error(t('collections:remove.error'));
    }
  }, [error, onClose, remove, success, t]);

  return (
    <Modal contentLabel={t('collections:remove.description')} isOpen={isOpen} onClose={onClose}>
      <Modal.Header title={t('collections:remove.header', { collectionName })} />
      <Modal.Body>{t('collections:remove.body')}</Modal.Body>
      <Modal.Footer>
        <Button isDisabled={isLoading} kind="ghost" onClick={onClose}>
          {t('collections:remove.abort')}
        </Button>
        <Button
          isLoading={isLoading}
          kind="danger"
          loadingText={t('collections:remove.progress')}
          onClick={handleRemove}
        >
          {t('collections:remove.confirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
